import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { ContactJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

/**
 * The activity feed is overwhelmingly machine-written: of the 1,000 most recent
 * rows, 585 are `Patient` and 405 are `Change`, both ownerless with payloads
 * like `Patient associated` or raw JSON. Everything else is something a person
 * or a lead actually did — except milestones, which are status bookkeeping
 * ("Closed Lost") rather than anyone reaching the contact.
 *
 * Excluded rather than allow-listed on purpose. An allowlist of Call and
 * FollowUp left the panel blank on 6 of 8 Active Leads, because a lead's most
 * recent real activity is usually a form or a note, and it would keep missing
 * whatever type the backend adds next.
 */
const NOISE_TYPES = [`Change`, `Patient`, `Milestone`]

/** One outreach event, normalised across the places outreach is recorded. */
export type LastTouch = {
  kind: `call` | `text` | `follow-up` | `other`
  /** The raw activity type, for kinds without a hand-written label. */
  rawType?: string
  /** Who did it, when the record names them outright. */
  actor?: string
  /** Who did it, when the record only carries an id to resolve. */
  actorUserId?: string
  /** Only calls have a direction worth showing. */
  inbound?: boolean
  /** What was actually said, when the record carries it. */
  snippet?: string
  /** The activity behind this touch, for jumping to it in the profile. */
  activityId?: string
  at: string
}

const TOUCH_FIELDS = `id type subType activityDate createdDate description note owner { id name }`

/**
 * A call activity's `description` is boilerplate ("Inbound Call"), not content:
 * the real substance lives on the Call record's AI summary. Quoting the
 * boilerplate back looks like a transcript and says nothing, so drop anything
 * that only restates the activity type.
 */
function meaningfulSnippet(hit: TouchActivity): string | undefined {
  const text = (hit.note || hit.description || ``).trim()
  if (!text) return undefined
  if (/^(in|out)bound\s+call$/i.test(text)) return undefined
  if (text.toLowerCase() === (hit.type ?? ``).toLowerCase()) return undefined
  return text
}

type TouchActivity = {
  id?: string
  type?: string
  subType?: string
  activityDate?: string
  createdDate?: string
  description?: string
  note?: string
  owner?: { id?: string; name?: string }
}

/**
 * The last time anyone reached this contact, and who.
 *
 * Outreach is recorded in three different places, so this reads the two that
 * are activities and lets the caller fold in the third:
 *
 * - `FollowUp` activities carry an owner on every row, so they name a person.
 * - `Call` activities carry a direction, and an owner only about 12% of the
 *   time, so the actor is often unknown and must not be invented.
 * - Texts are NOT activities at all (`type:"Text"` returns zero rows). They
 *   live on Message with `sentByUserId`, which the card already has loaded.
 *
 * Everything else in the activity feed is machine noise: over 30 days this
 * workspace logged 270 `Patient` and 120 `Change` rows against 9 `Call`, so
 * filtering to human outreach is the whole job here.
 */
export function useLastTouch(contact?: ContactJson): { touch?: LastTouch; loading: boolean } {
  const [touch, setTouch] = useState<LastTouch | undefined>(undefined)
  // Tracked so the panel can hold its tongue until the read lands, instead of
  // flashing "no activity" on every card and then correcting itself.
  const [loading, setLoading] = useState(false)
  const generation = useRef(0)
  const contactId = contact?.id
  const workspaceId = contact?.workspaceId
  const locationId = contact?.locationId

  useEffect(() => {
    generation.current += 1
    const token = generation.current
    setTouch(undefined)
    if (!contactId || !workspaceId || !locationId) {
      setLoading(false)
      return
    }
    setLoading(true)
    bg(
      gqlFetchList<TouchActivity>(
        `activities`,
        {
          search: `workspaceId:${workspaceId} locationId:${locationId} contacts.id.keyword:${contactId} AND NOT type:(${NOISE_TYPES.map(
            (type) => `"${type}"`,
          ).join(` OR `)})`,
          pagination: { limit: 1 },
          sort: { field: `activityDate`, direction: `DESC` },
        },
        TOUCH_FIELDS,
      )
        .then(({ hits }) => {
          if (token !== generation.current) return
          setLoading(false)
          const hit = hits?.[0]
          const at = hit?.activityDate ?? hit?.createdDate
          if (!hit || !at) return
          setTouch({
            kind: hit.type === `FollowUp` ? `follow-up` : hit.type === `Call` ? `call` : `other`,
            rawType: hit.type,
            actor: hit.owner?.name || undefined,
            inbound: hit.type === `Call` ? hit.subType === `inbound` : undefined,
            snippet: meaningfulSnippet(hit),
            activityId: hit.id,
            at,
          })
        })
        .catch((err) => {
          if (token !== generation.current) return
          setLoading(false)
          logError(`Action Center failed to load the last touch`, err)
        }),
    )
  }, [contactId, workspaceId, locationId])

  return { touch, loading }
}

/** Whichever of two touches happened most recently. */
export function latestTouch(a?: LastTouch, b?: LastTouch): LastTouch | undefined {
  if (!a) return b
  if (!b) return a
  return moment(a.at).isAfter(moment(b.at)) ? a : b
}
